import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { fetchProducts } from "../lib/api";
import { getProductName, getProductHighlights } from "../i18n";
import { ProductImg } from "./ProductImg";

// Flagship product showcase — restrained, editorial single-product spotlight.
// Uses a light editorial stage + object-contain so the cap breathes and the
// embroidery/fabric detail remain fully visible on every viewport.
export const CapShowcase = () => {
  const { t, lang } = useLanguage();
  const [product, setProduct] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    fetchProducts({ featured: true })
      .then((list) => setProduct(list[0]))
      .catch(() => setProduct(null));
  }, []);

  if (!product) return null;

  const highlights = getProductHighlights(product, lang);
  const productHref = `/product/${product.slug}`;
  const gallery = product.images?.slice(0, 4) || [];
  const activeSrc = gallery[activeIdx] || gallery[0];

  return (
    <section
      id="cap-showcase"
      data-testid="cap-showcase"
      className="relative bg-black py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-20">
          <div>
            <p className="overline mb-4">{t.showcase.overline}</p>
            <h2 className="h-display text-white text-4xl sm:text-5xl md:text-6xl max-w-2xl leading-[0.95]">
              {t.showcase.title}
            </h2>
          </div>
          <Link
            to={productHref}
            className="hidden md:inline-flex cta-line text-white/80 hover:text-white"
          >
            {t.showcase.viewProduct} <ArrowRight className="w-4 h-4 btn-arrow" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image column — no artificial white stage; container hugs the image */}
          <div className="lg:col-span-6">
            <div className="max-w-[560px] mx-auto lg:mx-0">
              <Link
                to={productHref}
                data-testid="showcase-primary-image"
                aria-label={getProductName(product, lang)}
                className="relative inline-block group"
              >
                {/* Image renders at its own natural aspect — no wrapping stage */}
                <ProductImg
                  src={activeSrc}
                  alt={getProductName(product, lang)}
                  className="block w-full h-auto max-h-[660px] object-contain transition-transform duration-[900ms] ease-out group-hover:scale-[1.02]"
                />
                {product.badge && (
                  <span className="absolute top-3 left-3 bg-yellow-400 text-black text-[9px] font-black tracking-[0.35em] uppercase px-2.5 py-1 shadow-sm">
                    {product.badge === "THE ORIGINAL"
                      ? t.hero.originalBadge
                      : product.badge}
                  </span>
                )}
              </Link>

              {/* Thumbnail row — small, consistent, no fake padded stage */}
              {gallery.length > 1 && (
                <div className="mt-5 flex gap-3">
                  {gallery.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveIdx(idx)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      data-testid={`showcase-thumb-${idx}`}
                      aria-label={`View ${idx + 1}`}
                      className={`relative block w-20 h-20 md:w-[88px] md:h-[88px] overflow-hidden transition-all duration-300 border ${
                        activeIdx === idx
                          ? "border-yellow-400 opacity-100"
                          : "border-white/15 opacity-60 hover:opacity-100 hover:border-white/40"
                      }`}
                    >
                      <ProductImg
                        src={img}
                        alt=""
                        className="w-full h-full object-cover"
                        small
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info column */}
          <div className="lg:col-span-6 flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-3">
              SKU {product.sku}
            </p>
            <h3 className="h-display text-white text-3xl md:text-4xl lg:text-5xl leading-tight">
              {getProductName(product, lang)}
            </h3>
            <p className="mt-5 flex items-baseline gap-2.5">
              <span className="h-display text-yellow-400 text-4xl md:text-5xl">
                ${product.price.toFixed(0)}
              </span>
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/50">
                USD
              </span>
            </p>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              {lang === "es" ? product.description_es : product.description}
            </p>

            {/* Highlight rows — editorial spec sheet, hairline dividers */}
            <ul className="mt-9 max-w-md border-t border-white/10">
              {highlights.slice(0, 5).map((h, i) => (
                <li
                  key={i}
                  data-testid={`showcase-highlight-${i}`}
                  className="py-3 border-b border-white/10 text-sm text-white/75 leading-relaxed"
                >
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-9">
              <Link
                to={productHref}
                data-testid="showcase-shop-button"
                className="btn-cabo btn-cabo--yellow"
              >
                {t.showcase.shopCta} <ArrowRight className="w-4 h-4 btn-arrow" />
              </Link>
              <Link
                to={productHref}
                data-testid="showcase-details-button"
                className="cta-line self-start sm:self-auto text-white/80 hover:text-white"
              >
                {t.showcase.details} <ArrowRight className="w-3.5 h-3.5 btn-arrow" />
              </Link>
            </div>

            {/* Availability — the product exists; online ordering opens soon */}
            <div data-testid="showcase-coming-soon" className="mt-8">
              <p className="font-display uppercase text-white text-xl md:text-2xl leading-none tracking-tight">
                {t.comingSoon.availHere}
              </p>
              <p className="mt-2.5 inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.3em] font-bold text-yellow-400">
                <span className="w-1.5 h-1.5 bg-yellow-400 animate-pulse" />
                {t.comingSoon.availOnline}
              </p>
            </div>

            <div className="mt-10 pt-6 border-t border-white/10 max-w-md flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/55">
                {t.showcase.trust1}
              </span>
              <span className="w-px h-3 bg-white/20" aria-hidden />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/55">
                {t.showcase.trust2}
              </span>
              <span className="w-px h-3 bg-white/20" aria-hidden />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/55 tabular-nums">
                {t.showcase.trust3}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
